/**
 * Financial Research Agent Tutorial - Advanced
 *
 * A 4-step financial research pipeline that teaches advanced Agent Commander
 * patterns: nested structured output schemas, parallel fan-out execution,
 * web search tools, and the agents-as-tools delegation pattern.
 *
 * Pipeline:
 *   User Prompt -> Planner Agent -> Search Agent (parallel fan-out) -> Writer Agent (agents-as-tools)
 *
 * Based on: backend/ai/openai/examples/financial_research_agent.py
 */
import { TutorialStep } from '../../types/tutorial';

const WORKFLOW_ROUTE_MSG =
  'To continue the tutorial, you need to be in the Workflow Editor.\n\n' +
  'Navigate to __Workflows__ in the sidebar and click on the "Financial Research Agent" workflow.';

/**
 * Tutorial values used for workflow/agent naming, instructions, and model definitions.
 */
export const FINANCIAL_RESEARCH_TUTORIAL_VALUES = {
  orchestration: {
    name: 'Financial Research Agent',
    description:
      'A 4-step pipeline that plans search queries, researches in parallel, and synthesizes a financial report with specialist sub-agents.',
  },
  agents: {
    planner: {
      name: 'Planner Agent',
      instructions:
        'You are a financial research planner. Given a request for financial analysis, produce a set of web searches to gather the context needed. Aim for recent headlines, earnings calls or 10-K snippets, analyst commentary, and industry background. Output between 5 and 15 search terms to query for.',
    },
    search: {
      name: 'Search Agent',
      instructions:
        'You are a research assistant specializing in financial topics. Given a search term, use web search to retrieve up-to-date context and produce a short summary of at most 300 words. Focus on key numbers, events, or quotes that will be useful to a financial analyst.',
    },
    financial: {
      name: 'Financial Agent',
      instructions:
        'You are a financial analyst focused on company fundamentals such as revenue, profit, margins and growth trajectory. Given a collection of web search results about a company, write a concise analysis of its recent financial performance. Pull out key metrics or quotes. Keep it under 2 paragraphs.',
    },
    risk: {
      name: 'Risk Agent',
      instructions:
        'You are a risk analyst looking for potential red flags in a company outlook. Given background research, produce a short analysis of risks such as competitive threats, regulatory issues, supply chain problems, or slowing growth. Keep it under 2 paragraphs.',
    },
    writer: {
      name: 'Writer Agent',
      instructions:
        'You are a senior financial analyst. You will be provided with the original query and a set of raw search summaries. Your task is to synthesize these into a long-form markdown report (at least several paragraphs) including a short executive summary and follow-up questions. If needed, you can call the available analysis tools (e.g. fundamentals_analysis, risk_analysis) to get short specialist write-ups to incorporate.',
    },
  },
  models: {
    searchItem: {
      className: 'FinancialSearchItem',
      description: 'A single search query with reasoning',
      properties: [
        { name: 'query', type: 'str' },
        { name: 'reason', type: 'str' },
      ],
    },
    searchPlan: {
      className: 'FinancialSearchPlan',
      description: 'A list of search queries to execute',
      properties: [{ name: 'searches', type: 'list[FinancialSearchItem]' }],
    },
    analysisSummary: {
      className: 'AnalysisSummary',
      description: 'A short specialist analysis',
      properties: [{ name: 'summary', type: 'str' }],
    },
    reportData: {
      className: 'FinancialReportData',
      description: 'The final structured research report',
      properties: [
        { name: 'short_summary', type: 'str' },
        { name: 'markdown_report', type: 'str' },
        { name: 'follow_up_questions', type: 'list[str]' },
      ],
    },
  },
  userPrompt: {
    label: 'Financial Query',
    inputMessage:
      'Enter a company or financial topic to research and I\'ll produce a comprehensive analyst report.',
  },
};

/**
 * Financial Research Agent Tutorial Steps (24 steps)
 *
 * Step Flow:
 * 1-3:   Welcome overview, create workflow
 * 4-6:   Add User Prompt step
 * 7-10:  Concept: Structured Output & Nested Schemas, create Planner Agent (pick Agent - Create)
 * 11-15: Concept: Step Intents, create Search Agent with Web Search, Concept: Parallel Fan-out
 * 16-19: Concept: Agents-as-Tools, create Writer Agent with sub-agents, Pipeline Review
 * 20-24: Run workflow, view output, explore Tool Usage tab, complete
 */
export const FINANCIAL_RESEARCH_TUTORIAL_STEPS: TutorialStep[] = [
  // =========================================================================
  // Phase 1: Introduction & Workflow Creation (Steps 1-3)
  // =========================================================================
  {
    stepNumber: 1,
    targetSelector: '[data-tutorial="nav-workflows"]',
    title: 'Welcome to Financial Research Pipelines!',
    description:
      "In this advanced tutorial, you'll build a __4-step financial research pipeline__ that combines every major Agent Commander pattern.\n\n" +
      "Think of it like a real research team:\n" +
      "1. A __manager__ plans what to research (structured output)\n" +
      "2. Multiple __researchers__ search the web simultaneously (parallel fan-out)\n" +
      "3. A __senior analyst__ writes the final report, consulting specialists as needed (agents-as-tools)\n\n" +
      "Along the way you'll learn:\n" +
      "- **Nested output schemas** (a schema that references another schema)\n" +
      "- **Parallel fan-out execution** (processing lists concurrently)\n" +
      "- **Web Search tools** (giving agents internet access)\n" +
      "- **Agents-as-Tools** (agents that call other agents)\n\n" +
      "Let's start by navigating to the Workflows page.",
    actionPrompt: "Click 'Workflows' in the sidebar to get started.",
    position: 'right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="nav-workflows"]',
    },
    expectedRoute: '/projects/:id',
  },
  {
    stepNumber: 2,
    targetSelector: '[data-tutorial="btn-create-workflow"]',
    title: 'Create the Workflow',
    description:
      "Let's create the workflow that will hold our financial research pipeline.",
    actionPrompt: "Click 'New Workflow' to create your workflow.",
    position: 'bottom',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-create-workflow"]',
    },
    expectedRoute: '/workflows',
    redirectMessage:
      'Navigate to __Workflows__ in the sidebar to continue.',
  },
  {
    stepNumber: 3,
    targetSelector: '[data-tutorial="form-workflow"]',
    title: 'Name Your Workflow',
    description:
      'Give your workflow a name and description:\n\n' +
      '__Name__: **Financial Research Agent**\n\n' +
      '__Description__: **A 4-step pipeline that plans search queries, researches in parallel, and synthesizes a financial report with specialist sub-agents.**',
    actionPrompt: "Fill in the fields and click 'Create'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-workflow"]',
    },
    expectedRoute: '/workflows',
    redirectMessage:
      'Navigate to __Workflows__ in the sidebar to continue.',
  },

  // =========================================================================
  // Phase 2: User Prompt (Steps 4-6)
  // =========================================================================
  {
    stepNumber: 4,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Add the User Prompt',
    description:
      "Every workflow starts with input. We'll add a __User Prompt__ step where the user enters their financial research query.\n\n" +
      "Click the __+__ button to add the first step.",
    actionPrompt: 'Click the __+__ button to add a step.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
  },
  {
    stepNumber: 5,
    targetSelector: '[data-tutorial="step-type-user_prompt"]',
    title: 'Select User Prompt',
    description:
      'Select the __User Prompt__ step type. This pauses the workflow and asks the user for input before continuing.',
    actionPrompt: 'Click on __User Prompt__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-user_prompt"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
    resumeFallbackStep: 4,
  },
  {
    stepNumber: 6,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Configure the User Prompt',
    description:
      "Configure what the user sees when the workflow runs:\n\n" +
      "__Label__: **Financial Query**\n\n" +
      "__Input Message__: **Enter a company or financial topic to research and I'll produce a comprehensive analyst report.**",
    actionPrompt: "Fill in the fields, then click 'Add Step'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
    resumeFallbackStep: 4,
  },

  // =========================================================================
  // Phase 3: Planner Agent with Nested Schemas (Steps 7-10)
  // =========================================================================
  {
    stepNumber: 7,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Concept: Structured Output & Nested Schemas',
    description:
      "Before we create the next step, let's understand a powerful concept.\n\n" +
      "**Structured Output** means the agent returns data in a specific shape instead of free-form text " +
      "- like filling out a form instead of writing an essay. You define a __schema__ with typed fields, " +
      "and the AI is constrained to return data matching that shape.\n\n" +
      "We're going to take this further with **Nested Schemas** - a schema that contains a *list* of another schema. " +
      "Our Planner Agent will output a `FinancialSearchPlan` containing multiple `FinancialSearchItem` entries, " +
      "each with a `query` and `reason` field.\n\n" +
      "This is how we get the Planner to output a structured *list* of searches that the next step can process.",
    actionPrompt: "Click the __+__ button to add the Planner Agent step.",
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
  },
  {
    stepNumber: 8,
    targetSelector: '[data-tutorial="step-option-run_agent-create"]',
    title: 'Select Agent - Create',
    description:
      "The Step Type picker has appeared. The Planner Agent *creates* a search plan from the user's prompt - it generates something new - so we'll use the __Create__ intent.",
    actionPrompt: 'Click on __Agent - Create__ to select it.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-run_agent-create"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
    resumeFallbackStep: 7,
  },
  {
    stepNumber: 9,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Create the Planner Agent',
    description:
      "The __Run Agent - Create__ step type is now selected, and the agent config modal has opened.\n\n" +
      "Now we need to create the Planner Agent with nested output schemas. Follow these steps:\n\n" +
      "__Create the agent:__\n" +
      "1. Click the __+__ next to the agent dropdown, then __Create from Scratch__\n" +
      "2. Name: **Planner Agent**\n" +
      '3. Instructions:\n' +
      "[[copy:Instructions:You are a financial research planner. Given a request for financial analysis, produce a set of web searches to gather the context needed. Aim for recent headlines, earnings calls or 10-K snippets, analyst commentary, and industry background. Output between 5 and 15 search terms to query for.]]\n\n" +
      "__Create the nested output schemas inline:__\n" +
      "4. Scroll to __Response Formatting__, set Output Type to __Structured Data (Schema)__\n" +
      "5. Click __Create New Schema__\n" +
      "6. Schema Name: **FinancialSearchItem**\n" +
      "7. Add property: name = **query**, type = **str**\n" +
      "8. Add property: name = **reason**, type = **str**\n" +
      "9. Click __Create Schema__\n" +
      "10. Click __Create New Schema__ again\n" +
      "11. Schema Name: **FinancialSearchPlan**\n" +
      "12. Add property: name = **searches**, type = **list[FinancialSearchItem]**\n" +
      "13. Click __Create Schema__\n" +
      "14. Select **FinancialSearchPlan** as the output schema\n" +
      "15. Click __Create Agent__\n\n" +
      "Click __Continue__ when the agent is created.",
    actionPrompt: 'Create the Planner Agent with nested output schemas.',
    position: 'screen-right',
    hideSpotlight: true,
    tooltipMaxWidth: 460,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
    resumeFallbackStep: 7,
  },
  {
    stepNumber: 10,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Add the Planner Step',
    description:
      "The **Planner Agent** should now be selected. When this step runs, it will receive the user's query " +
      "and output a `FinancialSearchPlan` - a structured list of search queries with reasons.\n\n" +
      "Click __Add Step__ to add the Planner to your workflow.",
    actionPrompt: "Click 'Add Step' to continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
    resumeFallbackStep: 7,
  },

  // =========================================================================
  // Phase 4: Search Agent with Web Search & Parallel Fan-out (Steps 11-15)
  // =========================================================================
  {
    stepNumber: 11,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Concept: Step Intents',
    description:
      "Before adding the next agent, let's understand **Step Intents**.\n\n" +
      "When you add a Run Agent step, you choose an __intent__ that describes what the agent does:\n\n" +
      "- **Create** - The agent generates something *new* from a prompt (like our Planner creating a search plan)\n" +
      "- **Enhance** - The agent *builds on* output from a previous step (like our Search Agent executing queries from the plan)\n" +
      "- **Validate** - The agent *checks or assesses* content (like a quality checker)\n" +
      "- **Assistant** - The agent *converses* with the user interactively\n\n" +
      "Our Search Agent will use __Enhance__ because it takes each search item from the Planner's output and enhances it with real web search results.",
    actionPrompt: "Click the __+__ button to add the Search Agent step.",
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
  },
  {
    stepNumber: 12,
    targetSelector: '[data-tutorial="step-option-run_agent-enhance"]',
    title: 'Select Agent - Enhance',
    description:
      "Select __Agent - Enhance__ for the Search Agent. Remember: Enhance means this agent builds on the previous step's output " +
      "(the Planner's search plan).",
    actionPrompt: 'Click on __Agent - Enhance__.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-run_agent-enhance"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
    resumeFallbackStep: 11,
  },
  {
    stepNumber: 13,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Create the Search Agent with Web Search',
    description:
      "Now create an agent that can search the internet. Follow these steps:\n\n" +
      "1. Click the __+__ next to the agent dropdown, then __Create from Scratch__\n" +
      "2. Name: **Search Agent**\n" +
      '3. Instructions:\n' +
      "[[copy:Instructions:You are a research assistant specializing in financial topics. Given a search term, use web search to retrieve up-to-date context and produce a short summary of at most 300 words. Focus on key numbers, events, or quotes that will be useful to a financial analyst.]]\n" +
      "4. Expand the __Tools__ section (click the arrow next to \"Tools\")\n" +
      "5. Click __Add Built-in Tool__\n" +
      "6. Click __Add Web Search Tool__ - this gives the agent access to search the internet in real time\n" +
      "7. Click __Create Agent__\n\n" +
      "**Web Search** is a built-in tool that lets agents query the internet for current information. " +
      "Without it, agents can only use their training data.",
    actionPrompt: 'Create the Search Agent with Web Search enabled, then click Continue.',
    position: 'screen-right',
    hideSpotlight: true,
    tooltipMaxWidth: 440,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
    resumeFallbackStep: 11,
  },
  {
    stepNumber: 14,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Add the Search Agent Step',
    description:
      "The **Search Agent** should now be selected with Web Search enabled.\n\n" +
      "Click __Add Step__ to add it to the workflow.",
    actionPrompt: "Click 'Add Step' to continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
    resumeFallbackStep: 11,
  },
  {
    stepNumber: 15,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Concept: Parallel Fan-out',
    description:
      "Now for one of the most powerful features: **Parallel Fan-out**.\n\n" +
      "The Planner outputs a *list* of search items (remember the `FinancialSearchPlan` schema with its `searches` list). " +
      "By default, Agent Commander would process each item one at a time. But with **Parallel** execution mode, " +
      "it sends *each item* to a separate Search Agent instance __simultaneously__.\n\n" +
      "Imagine dispatching 10 researchers to the library at once instead of sending them one after another " +
      "- the work completes much faster.\n\n" +
      "To enable this:\n" +
      "1. Click on the __Search Agent step__ in the workflow canvas\n" +
      "2. In the __Properties Panel__ on the right, find __Execution Mode__\n" +
      "3. Change it from __Synchronous__ to __Parallel__\n" +
      "4. Click __Save Changes__ to apply\n\n" +
      "Click __Continue__ once you've saved the changes.",
    actionPrompt: "Set the Search Agent step to Parallel execution, save changes, then click Continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
  },

  // =========================================================================
  // Phase 5: Writer Agent with Agents-as-Tools (Steps 16-19)
  // =========================================================================
  {
    stepNumber: 16,
    targetSelector: '[data-tutorial="btn-add-step"]',
    title: 'Concept: Agents-as-Tools',
    description:
      "Time for the final and most sophisticated pattern: **Agents-as-Tools**.\n\n" +
      "So far, our agents work in a fixed sequence - each step always runs. But what if an agent needs to " +
      "*decide on its own* whether to consult a specialist?\n\n" +
      "**Agents-as-Tools** lets you attach other agents as callable tools to an orchestrator agent. " +
      "The orchestrator decides __when and whether__ to invoke them based on the conversation.\n\n" +
      "Our Writer Agent will have two specialist tools:\n" +
      "- **Financial Agent** - analyzes company fundamentals (revenue, margins, growth)\n" +
      "- **Risk Agent** - identifies red flags (competition, regulation, supply chain)\n\n" +
      "The Writer can call both, one, or neither depending on what the research requires " +
      "- just like a senior analyst who can optionally consult domain experts.",
    actionPrompt: "Click the __+__ button to add the Writer Agent step.",
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-add-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
  },
  {
    stepNumber: 17,
    targetSelector: '[data-tutorial="step-option-run_agent-enhance"]',
    title: 'Select Agent - Enhance',
    description:
      "Select __Agent - Enhance__ again. The Writer Agent enhances the Search Agent's collected summaries " +
      "into a comprehensive report.",
    actionPrompt: 'Click on __Agent - Enhance__.',
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="step-option-run_agent-enhance"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
    resumeFallbackStep: 16,
  },
  {
    stepNumber: 18,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Create the Writer Agent & Sub-Agents',
    description:
      "This is the most complex step - you'll create the Writer Agent with its output schema *and* two specialist sub-agents attached as tools. Everything is done inline from the agent form.\n\n" +
      "__Create the Writer Agent:__\n" +
      "1. Click __+__ next to agent dropdown, then __Create from Scratch__\n" +
      "2. Name: **Writer Agent**\n" +
      "3. Instructions:\n" +
      "[[copy:Instructions:You are a senior financial analyst. You will be provided with the original query and a set of raw search summaries. Your task is to synthesize these into a long-form markdown report (at least several paragraphs) including a short executive summary and follow-up questions. If needed, you can call the available analysis tools (e.g. fundamentals_analysis, risk_analysis) to get short specialist write-ups to incorporate.]]\n\n" +
      "__Create the output schema inline:__\n" +
      "4. Scroll to __Response Formatting__, set Output Type to __Structured Data (Schema)__\n" +
      "5. Click __Create New Schema__\n" +
      "6. Schema Name: **FinancialReportData**\n" +
      "7. Add properties: `short_summary` (str), `markdown_report` (str), `follow_up_questions` (list[str])\n" +
      "8. Click __Create Schema__, then select **FinancialReportData**\n\n" +
      "__Attach the specialist sub-agents as tools:__\n" +
      "9. Expand __Tools__ section, click __Add Tool Agent__\n" +
      "10. Click __+__ to create **Financial Agent** inline:\n" +
      "[[copy:Financial Agent Instructions:You are a financial analyst focused on company fundamentals such as revenue, profit, margins and growth trajectory. Given a collection of web search results about a company, write a concise analysis of its recent financial performance. Pull out key metrics or quotes. Keep it under 2 paragraphs.]]\n" +
      "   - In Response Formatting, set Output Type to __Structured Data (Schema)__\n" +
      "   - Click __Create New Schema__: name **AnalysisSummary**, add property `summary` (str), click __Create Schema__\n" +
      "   - Select **AnalysisSummary**, then click __Create Agent__\n" +
      "   - Set tool name: **financial_agent_tool**, description: **Analyze company financial fundamentals**\n" +
      "11. Click __Add Tool__, then click __Add Tool Agent__ again\n" +
      "12. Click __+__ to create **Risk Agent** inline:\n" +
      "[[copy:Risk Agent Instructions:You are a risk analyst looking for potential red flags in a company outlook. Given background research, produce a short analysis of risks such as competitive threats, regulatory issues, supply chain problems, or slowing growth. Keep it under 2 paragraphs.]]\n" +
      "   - In Response Formatting, set Output Type to __Structured Data (Schema)__\n" +
      "   - Select the existing **AnalysisSummary** schema (already created for Financial Agent)\n" +
      "   - Click __Create Agent__\n" +
      "   - Set tool name: **risk_agent_tool**, description: **Analyze risks and red flags**\n" +
      "13. Click __Add Tool__\n" +
      "14. Click __Create Agent__\n\n" +
      "Click __Continue__ when the Writer Agent is created with both tool agents attached.",
    actionPrompt: 'Create the Writer Agent with inline schemas and both specialist sub-agents as tools.',
    position: 'screen-right',
    hideSpotlight: true,
    tooltipMaxWidth: 480,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
    resumeFallbackStep: 16,
  },
  {
    stepNumber: 19,
    targetSelector: '[data-tutorial="modal-step-config"]',
    title: 'Add the Writer Agent Step',
    description:
      "The **Writer Agent** should now be selected with both tool agents attached.\n\n" +
      "Click __Add Step__ to complete your pipeline.",
    actionPrompt: "Click 'Add Step' to continue.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-step"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
    resumeFallbackStep: 16,
  },
  {
    stepNumber: 20,
    targetSelector: '[data-tutorial="btn-run-workflow"]',
    title: 'Pipeline Complete!',
    description:
      "Congratulations - you've built the complete Financial Research pipeline!\n\n" +
      "Let's review what you've assembled:\n\n" +
      "1. **User Prompt** - Collects the research query\n" +
      "2. **Planner Agent** - Creates a structured search plan (__nested schemas__)\n" +
      "3. **Search Agent** - Executes searches in __parallel fan-out__ with __web search__\n" +
      "4. **Writer Agent** - Synthesizes the report using __agents-as-tools__ (Financial Agent & Risk Agent)\n\n" +
      "This combines __every advanced pattern__ in Agent Commander into a single, powerful workflow. " +
      "Let's run it and see the results!",
    actionPrompt: "Click 'Execute' to run your pipeline!",
    position: 'screen-right',
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-run-workflow"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
  },

  // =========================================================================
  // Phase 6: Execute & Explore Results (Steps 21-24)
  // =========================================================================
  {
    stepNumber: 21,
    targetSelector: '[data-tutorial="btn-submit-user-input"]',
    title: 'Enter a Research Query',
    description:
      "Enter a financial research topic. Try something like:\n\n" +
      "**Analyze Nvidia's latest quarterly earnings and outlook**\n\n" +
      "The pipeline will:\n" +
      "1. Plan 5-15 search queries\n" +
      "2. Execute them all in parallel\n" +
      "3. Synthesize a comprehensive analyst report\n\n" +
      "This may take a minute since multiple agents are working.",
    actionPrompt: "Enter your query and click 'Submit'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'click',
      value: '[data-tutorial="btn-submit-user-input"]',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
  },
  {
    stepNumber: 22,
    targetSelector: '[data-tutorial="btn-view-final-output"]',
    title: 'View the Final Output',
    description:
      "Your pipeline is running! Once it completes, click __View Final Output__ to see the Writer Agent's report.\n\n" +
      "The output is __structured__ thanks to the `FinancialReportData` schema:\n" +
      "- **short_summary** - A brief executive overview\n" +
      "- **markdown_report** - The full analysis in markdown\n" +
      "- **follow_up_questions** - Suggested next research directions\n\n" +
      "This is the power of structured output - predictable, parseable results that can be used by other systems.",
    actionPrompt: "Click 'View Final Output' when the workflow completes.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
  },
  {
    stepNumber: 23,
    targetSelector: '[data-tutorial="btn-view-final-output"]',
    title: 'Explore the Tool Usage Tab',
    description:
      "Now click the __Tool Usage__ tab in the output panel (between Input and Metadata).\n\n" +
      "This tab shows exactly which tools the Writer Agent called during execution. You should see:\n\n" +
      "- **financial_agent_tool** - The Writer consulted the Financial Agent for fundamental analysis\n" +
      "- **risk_agent_tool** - The Writer consulted the Risk Agent for risk assessment\n\n" +
      "Each entry shows the __arguments__ the Writer sent and the __result__ that came back. " +
      "This is how agents-as-tools work behind the scenes - the Writer *decided* when to call each specialist " +
      "based on the research material.\n\n" +
      "Try clicking on different steps in the output to see their individual tool calls too.",
    actionPrompt: "Explore the Tool Usage tab, then click 'Continue'.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
  },
  {
    stepNumber: 24,
    targetSelector: '[data-tutorial="btn-view-final-output"]',
    title: 'Tutorial Complete!',
    description:
      "Congratulations! You've built a production-grade financial research pipeline.\n\n" +
      "__What you've learned:__\n" +
      "- **Structured Output Schemas** - Constraining agent responses to typed data shapes\n" +
      "- **Nested Schemas** - A schema containing a list of another schema (`FinancialSearchPlan` with `list[FinancialSearchItem]`)\n" +
      "- **Step Intents** - Create vs Enhance and when to use each\n" +
      "- **Web Search Tools** - Giving agents real-time internet access\n" +
      "- **Parallel Fan-out** - Processing list items concurrently for speed\n" +
      "- **Agents-as-Tools** - Letting an orchestrator decide when to consult specialists\n" +
      "- **Tool Usage Tab** - Inspecting which tools agents called during execution\n\n" +
      "You can now customize this pipeline - try adding more specialist agents, changing the search count, " +
      "or adapting it for different research domains.\n\n" +
      "__Tip:__ This is a heavy pipeline to rebuild. If you want a sandbox to experiment in without " +
      "touching the original, open the Projects page and click the __Clone__ icon on the project card - " +
      "you get a deep copy of every agent, model, queue, and workflow in this project, fully wired up. " +
      "For a smaller change, the __Duplicate__ icon on the Workflows page copies just this workflow " +
      "(its agents and models come along too).\n\n" +
      "Click __Finish Tutorial__ when you're ready!",
    actionPrompt: "Click 'Finish Tutorial' to complete.",
    position: 'screen-right',
    hideSpotlight: true,
    completionTrigger: {
      type: 'manual',
      value: '',
    },
    expectedRoute: '/workflows/:id',
    redirectMessage: WORKFLOW_ROUTE_MSG,
  },
];
